import { toast } from 'ui/sonner'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { apiClient } from '~/api/client'
import { LS_JWT_KEY } from '~/const/api'
import i18n from '~/i18n'
import { getNewLocalLanguagesPairs } from '~/utils/get-new-local-languages-pairs'
import { syncLocalLanguagesPairs } from '~/utils/sync-local-languages-pairs'

export type Account = {
  coins: number
  email: string
  id: number
  username: string | null
  webauthnRegistered: boolean
  languages?: Record<string, string[]> | null
  lastSelectedLanguages?: [string, string]
  planCoins?: number | null
  planId?: number | null
}

type AccountActions = {
  addLanguagePair: (source: string, learn: string) => void
  clearAccount: () => void
  decreaseCoins: (decreaseBy: number) => void
  patchAccount: (account: Partial<Account>) => void
  revalidateAccount: () => Promise<void>
}

// Account fields are only read inside authenticated app routes (MainShell).
// After login or revalidation the store is populated; clearAccount resets it to empty.
export const useAccountStore = create<Account & AccountActions>()(
  persist(
    (set, get) =>
      ({
        patchAccount: account => set(account),
        clearAccount: () => {
          useAccountStore.persist.clearStorage()
          set(
            state => ({
              ...({} as Account),
              addLanguagePair: state.addLanguagePair,
              clearAccount: state.clearAccount,
              decreaseCoins: state.decreaseCoins,
              patchAccount: state.patchAccount,
              revalidateAccount: state.revalidateAccount,
            }),
            true,
          )
        },
        addLanguagePair: (source, learn) =>
          set(state => {
            const learnLanguages = state?.languages?.[source] ?? []
            if (learnLanguages.includes(learn)) return state

            const languages = { ...state.languages, [source]: [...learnLanguages, learn] }

            return { languages }
          }),
        revalidateAccount: async () => {
          const language = i18n.language.split('-')[0] || 'en'
          try {
            const { data, response } = await apiClient.GET('/user/me', {
              params: {
                header: {
                  'x-timezone-offset': (-new Date().getTimezoneOffset() / 60).toString(),
                  'x-app-locale': language.split('-')[0] || 'en',
                },
              },
            })

            if (response.status >= 400) return

            if (data?.jwt) localStorage.setItem(LS_JWT_KEY, data?.jwt)

            if (!data?.user) return

            const localLastSelectedLanguages = get().lastSelectedLanguages

            const newLocalLanguagesPairs = getNewLocalLanguagesPairs(
              get().languages,
              data.user.languages,
              localLastSelectedLanguages,
            )

            void syncLocalLanguagesPairs(newLocalLanguagesPairs)

            const lastSelectedLanguages = localLastSelectedLanguages ?? data.user.lastSelectedLanguages ?? undefined

            set({
              ...data.user,
              lastSelectedLanguages: lastSelectedLanguages as [string, string],
              coins: data.user.coins ?? 0,
            })
          } catch (error) {
            toast.error(i18n.t('Network error'))
            console.error({ message: 'Network error', error })
            return
          }
        },
        decreaseCoins: (decreaseBy: number) => set(state => ({ coins: state.coins - decreaseBy })),
      }) as Account & AccountActions,
    { name: 'lingo-cards-account' },
  ),
)
